import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Breadcrumbs, P, H6 } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

//TYPE AND SERVICES

// COMPANY MASTER
import {
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

// ADDRESS
import {
  countryService,
  COUNTRY_QUERY_KEY,
} from "../../../../../Services/countryService";
import { Country } from "../../../../../Types/MasterData/Country";
import {
  provinceService,
  PROVINCE_QUERY_KEY,
} from "../../../../../Services/provinceService";
import { Province } from "../../../../../Types/MasterData/Province";
import {
  cityService,
  CITY_QUERY_KEY,
} from "../../../../../Services/cityService";
import { City } from "../../../../../Types/MasterData/City";
import {
  barangayService,
  BARANGAY_QUERY_KEY,
} from "../../../../../Services/barangayService";
import { Barangay } from "../../../../../Types/MasterData/Barangay";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  company_code: string;
  company_name: string;
}

const UpdateAddressModal: React.FC<UpdateFormProps> = ({
  toggle,
  company_code,
  company_name,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    company_code: company_code,
    company_name: company_name,
    address_line1: "",
    address_line2: "",
    country: "",
    province: "",
    city: "",
    barangay: "",
    zip_code: "",
  });

  //FETCH COMPANY DETAILS
  const { data: CompanyData, isLoading: isFetching } = useQuery({
    queryKey: [COMPANY_QUERY_KEY, company_code],
    queryFn: () => companyprofileService.getCompanyProfile(company_code),
  });

  useEffect(() => {
    if (CompanyData) {
      setFormData({
        company_code: CompanyData.data.company_code || company_code,
        company_name: CompanyData.data.company_name || company_name,
        address_line1: CompanyData.data.address_line1 || "",
        address_line2: CompanyData.data.address_line2 || "",
        country: "",
        province: "",
        city: "",
        barangay: "",
        zip_code: "",
      });
    }
  }, [CompanyData]);

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [provinceId, setProvinceId] = useState<string | null>(null);
  const [cityId, setCityId] = useState<string | null>(null);

  //LIST OF VALUE
  const { data: countryData, isLoading: isCountryLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });
  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  const {
    data: provinceData,
    isLoading: isProvinceLoading,
    isError: isProvinceError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryCode],
    queryFn: () => provinceService.getProvinceByCountry(countryCode as string),
    enabled: !!countryCode,
  });

  const provinces: Province[] =
    provinceData?.data?.filter((item) => item.status === 1) ?? [];

  const provinceOptions = !isProvinceError
    ? provinces.map((prov) => ({
        id: prov.id,
        label: prov.name,
      }))
    : [];

  const {
    data: cityData,
    isLoading: isCityLoading,
    isError: isCityError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceId],
    queryFn: () => cityService.getCityByProvince(provinceId as string),
    enabled: !!provinceId,
  });

  const cities: City[] =
    cityData?.data?.filter((item) => item.status === 1) ?? [];

  const cityOptions = !isCityError
    ? cities.map((city) => ({
        id: city.id,
        label: city.name,
      }))
    : [];

  const {
    data: barangayData,
    isLoading: isBarangayLoading,
    isError: isBarangayError,
  } = useQuery<{ data: Barangay[] }>({
    queryKey: [BARANGAY_QUERY_KEY, cityId],
    queryFn: () => barangayService.getBarangayByCity(cityId as string),
    enabled: !!cityId,
  });

  const barangay: Barangay[] =
    barangayData?.data?.filter((item) => item.status === 1) ?? [];

  const barangayOptions = !isBarangayError
    ? barangay.map((barangay) => ({
        id: barangay.id,
        label: barangay.name,
      }))
    : [];

  const handleProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province: "",
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setCountryCode(e.target.value);
    setProvinceId("");
    setCityId("");
  };

  const handleCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setProvinceId(e.target.value);
    setCityId("");
  };

  const handleBarangaySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      barangay: "",
      zip_code: "",
    }));
    setCityId(e.target.value);
  };

  const updateCompanyMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      companyprofileService.updateCompanyProfile(company_code, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      toast.success(
        "The company business address has been successfully updated.",
      );
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update client profile.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateCompanyMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <H6 className="fw-bold">Company ing Address</H6>

          <Col md="12">
            <Label>
              Address Line 1<b className="text-danger">*</b>
            </Label>
            <Input
              id="address_line1"
              name="address_line1"
              type="text"
              placeholder="Street / House / Building"
              maxLength={150}
              minLength={5}
              required
              value={formData.address_line1}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>Address Line 2</Label>
            <Input
              id="address_line2"
              name="address_line2"
              type="text"
              placeholder="Apartment / Unit / Subdivision"
              maxLength={150}
              minLength={5}
              value={formData.address_line2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>
              Country<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2 select-scroll"
              value={formData.country}
              id="country"
              name="country"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleProvinceSelect(e);
              }}
              required
            >
              <option value="" disabled>
                {isCountryLoading ? "Loading..." : "Select Country"}
              </option>
              {!isCountryLoading &&
                countries.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              Province<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.province}
              id="province"
              name="province"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleCitySelect(e);
              }}
              required
              disabled={isProvinceLoading || !countryCode || isProvinceError}
            >
              <option value="" selected>
                {isProvinceLoading
                  ? "Loading..."
                  : isProvinceError
                    ? "No Province found"
                    : "Select Province"}
              </option>

              {!isProvinceLoading &&
                provinceOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              City/Municipality<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.city}
              id="city"
              name="city"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleBarangaySelect(e);
              }}
              required
              disabled={isCityLoading || !provinceId || isCityError}
            >
              <option value="" disabled selected>
                {isCityLoading
                  ? "Loading..."
                  : isCityError
                    ? "No City Found"
                    : "Select City"}
              </option>

              {!isCityLoading &&
                cityOptions.map((opt) => (
                  <option key={opt.id || "empty"} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              Barangay<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.barangay}
              id="barangay"
              name="barangay"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
              }}
              required
              disabled={isBarangayLoading || !cityId || isBarangayError}
            >
              <option value="" disabled selected>
                {isBarangayLoading
                  ? "Loading..."
                  : isBarangayError
                    ? "No Barangay Found"
                    : "Select Barangay"}
              </option>

              {!isBarangayLoading &&
                barangayOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              ZIP Code<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              id="zip_code"
              name="zip_code"
              type="text"
              placeholder="XXXX"
              maxLength={4}
              minLength={4}
              value={formData.zip_code}
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateCompanyMutation.isPending}
            >
              {updateCompanyMutation.isPending
                ? "Updating Client Profile..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateAddressModal;
